// V4 professions: unlike V1-3's leveled-skill system (character-progression-
// v2.mjs, still used by legacy V3 code), V4 is a 30-60 min roguelike with no
// room for stat progression. A profession just tags a survivor with one
// category, and every profession in that category grants the same run-scoped
// unlock (see plans/i-think-i-want-rosy-stream.md). This file is a clean,
// V4-only fork of the old profession list rather than a shared/conditional
// one — trimmed to the professions that are actually plausible/interesting
// for their category, plus new categories the old list never had.
//
// "none" is the deliberately common, mechanically-inert case: most survivors
// have no specialty, so finding an actual specialist stays rare. Today the
// none-pool (21 names) is smaller than the sum of the category pools (34
// names) — the opposite of "rare" — worth revisiting (more none-flavor, or
// weighting the random pick toward none) once this is played more.
//
// A handful of professions also carry `poiReveal`: a low-value "hometown
// connection" perk that points at one real building on the map (see
// domain/poi-reveal.mjs) — "oh, that's the auto shop the mechanic used to
// work at." Scaffolding only: 4 professions have it (priest/youth-group-
// leader -> a church, mechanic -> an auto shop, kindergarten-teacher -> a
// school). Extend the same way for more — pick a profession, pick an OSM
// tag/value it plausibly maps to, add a `poiReveal` matcher.

export const PROFESSION_CATEGORIES = [
  "medical",
  "electrician",
  "construction",
  "driving",
  "cooking",
  "farmer",
  "locksmith",
  "none",
];

// What each category actually does, for the in-game profession-pill popup.
// `implemented: false` categories are real design (see the plan doc) but not
// yet wired into the sim — room construction isn't a reachable player action
// yet, so Medical/Electrician/Construction/Cooking/Farmer/Driving unlocks
// have nothing to hook into today. Locksmith is the first (and so far only)
// fully-wired unlock.
export const PROFESSION_CATEGORY_DETAILS = {
  medical: {
    label: "Medical",
    summary: "Unlocks a Clinic — fully heals one survivor per day once built.",
    implemented: false,
  },
  electrician: {
    label: "Electrician",
    summary: "Unlocks a Battery Room, and guarantees a component drop when scavenging matching buildings.",
    implemented: false,
  },
  construction: {
    label: "Construction",
    summary: "Reinforces barricades and walls, making them harder to breach during a base defense.",
    implemented: false,
  },
  driving: {
    label: "Driving",
    summary: "Improves the squad's odds on the road — a safer or faster trip to and from a supply run.",
    implemented: false,
  },
  cooking: {
    label: "Cooking",
    summary: "Unlocks a Kitchen, cutting the base's food consumption by 20%.",
    implemented: false,
  },
  farmer: {
    label: "Farmer",
    summary: "Unlocks a Garden, producing food for the base every day.",
    implemented: false,
  },
  locksmith: {
    label: "Locksmith",
    summary: "During supply runs, this survivor's searches never generate threat — locked containers never make noise.",
    implemented: true,
  },
  none: {
    label: "No Specialty",
    summary: "No unlock — just an ordinary person along for the ride.",
    implemented: true,
  },
};

const MEDICAL_PROFESSIONS = [
  {
    id: "icu-nurse",
    name: "ICU Nurse",
    category: "medical",
    traits: [],
    notes: "Ran procedures like a metronome while the rest of the team read the labels too slowly.",
  },
  {
    id: "paramedic",
    name: "Paramedic",
    category: "medical",
    traits: ["calm"],
    notes: "Arrived calm, triaged fast, and had no interest in your dramatic entrance plan.",
  },
  {
    id: "er-doctor",
    name: "ER Doctor",
    category: "medical",
    traits: ["calm"],
    notes: "Turned chaos into protocol and still somehow found a dry comment about vitals.",
  },
  {
    id: "4th-year-med-student",
    name: "4th Year Med Student",
    category: "medical",
    traits: ["quick learner"],
    notes: "Had almost finished, stayed mostly caffeinated, and absorbed complex cases fast enough to call out 'that's a subtle finding.'",
  },
  {
    id: "pediatric-cardiologist",
    name: "Pediatric Cardiologist",
    category: "medical",
    traits: ["observant"],
    notes: "Spent a career catching the too-faint, too-fast heartbeats hiding in kids who looked perfectly fine; nothing about triage surprises them anymore.",
  },
];

const CONSTRUCTION_PROFESSIONS = [
  {
    id: "handyman",
    name: "Handyman",
    category: "construction",
    traits: ["charming"],
    notes: "Did odd jobs across half the neighborhood and somehow made landlords trust them with all of your broken things.",
  },
  {
    id: "utility-plumber",
    name: "Utility Plumber",
    category: "construction",
    traits: ["calm"],
    notes: "Turned panic into shutoff procedures and kept pipes from turning every outage into a crisis.",
  },
  {
    id: "building-inspector",
    name: "Building Inspector",
    category: "construction",
    traits: ["observant", "calm"],
    notes: "Inspected structures like a hawk, spotting weak angles and hidden risks before anyone else noticed the floor had already started speaking.",
  },
  {
    id: "general-contractor",
    name: "General Contractor",
    category: "construction",
    traits: [],
    notes: "Coordinated crews and materials, and somehow made a chaotic project run like a regular Tuesday.",
  },
  {
    id: "structural-field-engineer",
    name: "Structural Field Engineer",
    category: "construction",
    traits: ["abrasive"],
    notes: "Had a reputation for standards nobody wanted to debate and never stopped arguing about load paths.",
  },
];

const DRIVING_PROFESSIONS = [
  {
    id: "soccer-mom",
    name: "Soccer Mom",
    category: "driving",
    traits: ["charming"],
    notes: "Ran three kids, two carpools, and a snack schedule out of one minivan; parallel parked it anywhere, apologized to no one.",
  },
  {
    id: "ride-share-driver",
    name: "Ride Share Driver",
    category: "driving",
    traits: ["observant"],
    notes: "Knew every shortcut, surge zone, and pothole in town, and could read a passenger's mood before the door closed.",
  },
  {
    id: "city-bus-driver",
    name: "City Bus Driver",
    category: "driving",
    traits: ["calm"],
    notes: "Threaded forty feet of bus through double-parked chaos on a schedule, unbothered by anything short of a full road closure.",
  },
  {
    id: "stunt-driver",
    name: "Stunt Driver",
    category: "driving",
    traits: ["calm", "athletic"],
    notes: "Rolled cars for a living and walked away grinning; the apocalypse finally made the reverse-180 a commute skill.",
  },
];

const ELECTRICIAN_PROFESSIONS = [
  {
    id: "self-driving-car-sensors-engineer",
    name: "Self-Driving Car Sensors Engineer",
    category: "electrician",
    traits: ["observant"],
    notes: "Calibrated lidar and camera arrays for cars that were supposed to drive themselves into a better future; now recalibrates instinct instead.",
  },
  {
    id: "rooftop-solar-installer",
    name: "Rooftop Solar Installer",
    category: "electrician",
    traits: ["athletic"],
    notes: "Spent years walking roof pitches with a harness and a torque wrench, unbothered by heights that would put anyone else on their knees.",
  },
  {
    id: "substation-lineman",
    name: "Substation Lineman",
    category: "electrician",
    traits: ["calm"],
    notes: "Worked live lines for a living and treated every emergency like just another Tuesday shift.",
  },
  {
    id: "ev-charging-technician",
    name: "EV Charging Station Technician",
    category: "electrician",
    traits: ["resourceful"],
    notes: "Kept half-broken charging stalls running with zip ties and stubbornness long after the manufacturer stopped answering calls.",
  },
  {
    id: "elevator-repair-tech",
    name: "Elevator Repair Tech",
    category: "electrician",
    traits: ["quick learner"],
    notes: "Learned every building's electrical quirks from the inside of the shaft, and never once got stuck for long.",
  },
];

const COOKING_PROFESSIONS = [
  {
    id: "michelin-star-saucier",
    name: "Michelin-Star Saucier",
    category: "cooking",
    traits: ["abrasive"],
    notes: "Ran a pass like a battlefield and plated like it was still being judged.",
  },
  {
    id: "pastry-chef",
    name: "Pastry Chef",
    category: "cooking",
    traits: ["resourceful"],
    notes: "Turned three ingredients and a deadline into something people used to pay forty dollars for.",
  },
  {
    id: "cafeteria-cook",
    name: "High School Cafeteria Cook",
    category: "cooking",
    traits: ["calm"],
    notes: "Fed six hundred teenagers a day on a budget that made triage look generous, and never once ran out.",
  },
  {
    id: "food-truck-owner-operator",
    name: "Food Truck Owner-Operator",
    category: "cooking",
    traits: ["charming"],
    notes: "Built a menu, a following, and a generator repair habit out of one cramped kitchen on wheels.",
  },
  {
    id: "competitive-bbq-pitmaster",
    name: "Competitive BBQ Pitmaster",
    category: "cooking",
    traits: ["observant"],
    notes: "Could read a fire by its smoke alone, and treated twelve-hour cook times as a matter of principle.",
  },
];

const FARMER_PROFESSIONS = [
  {
    id: "community-garden-organizer",
    name: "Community Garden Organizer",
    category: "farmer",
    traits: ["charming"],
    notes: "Talked an entire block into turning a vacant lot into rows of tomatoes, then talked them into weeding it.",
  },
  {
    id: "backyard-beekeeper",
    name: "Backyard Beekeeper",
    category: "farmer",
    traits: ["calm"],
    notes: "Worked a hive barehanded and rarely got stung; everyone else's panic was the bigger risk factor.",
  },
  {
    id: "homesteader",
    name: "Homesteader",
    category: "farmer",
    traits: ["resourceful"],
    notes: "Canned, composted, and repaired things out of principle long before it was a survival requirement.",
  },
  {
    id: "rooftop-hydroponics-hobbyist",
    name: "Rooftop Hydroponics Hobbyist",
    category: "farmer",
    traits: ["observant"],
    notes: "Obsessed over pH levels and grow lights on an apartment roof, and somehow always had fresh basil.",
  },
  {
    id: "retired-dairy-farmer",
    name: "Retired Dairy Farmer",
    category: "farmer",
    traits: ["anxious"],
    notes: "Spent forty years getting up before dawn for animals that never once said thank you; the new schedule feels almost familiar.",
  },
];

const LOCKSMITH_PROFESSIONS = [
  {
    id: "repo-man",
    name: "Repo Man",
    category: "locksmith",
    traits: ["abrasive"],
    notes: "Got into cars people didn't want opened for a living, and never lost sleep over being unpopular.",
  },
  {
    id: "escape-room-designer",
    name: "Escape Room Designer",
    category: "locksmith",
    traits: ["resourceful"],
    notes: "Built puzzles out of locks and hinges for a living; picking the real ones is just faster now, without an audience.",
  },
  {
    id: "storage-unit-auctioneer",
    name: "Storage Unit Auctioneer",
    category: "locksmith",
    traits: ["observant"],
    notes: "Could size up a locked unit's contents through a two-inch gap and a flashlight, sight unseen.",
  },
  {
    id: "vault-technician",
    name: "Vault Technician",
    category: "locksmith",
    traits: ["calm"],
    notes: "Serviced bank vaults for a living and treated every stuck bolt as a puzzle, not a crisis.",
  },
  {
    id: "locksport-hobbyist",
    name: "Locksport Hobbyist",
    category: "locksmith",
    traits: ["quick learner"],
    notes: "Picked locks for sport on weekends, mostly for the satisfaction of a pin stack falling into place.",
  },
];

// Mechanically inert on purpose — most survivors are ordinary people, so
// finding an actual specialist stays rare and exciting. Ported from the V3
// list's zero-skill and computers/dispatch tracks (dropped there since V4
// doesn't do skill levels), kept here purely for flavor variety.
const FLAVOR_PROFESSIONS = [
  {
    id: "student",
    name: "Student",
    category: "none",
    traits: ["quick learner"],
    notes: "Was still in learning mode when the world changed, and never got to finish the syllabus.",
  },
  {
    id: "athlete",
    name: "Athlete",
    category: "none",
    traits: [],
    notes: "Could run marathons and set personal records, but this was a different kind of endurance test.",
  },
  {
    id: "banker",
    name: "Banker",
    category: "none",
    traits: ["abrasive"],
    notes: "Sold stress and leverage with confidence; now trying to turn ration lines into something they can forecast.",
  },
  {
    id: "lawyer",
    name: "Lawyer",
    category: "none",
    traits: ["charming"],
    notes: "Could have won every procedural argument, then learned a broken world has fewer rules and worse hearing.",
  },
  {
    id: "management-consultant",
    name: "Management Consultant",
    category: "none",
    traits: ["observant"],
    notes: "Could map any business to a process chart; the apocalypse came with none of the spreadsheets or buy-in.",
  },
  {
    id: "influencer",
    name: "Influencer",
    category: "none",
    traits: ["slow learner"],
    notes: "Had a polished public presence, a billion likes, and discoverer-level timing once the network stayed lit.",
  },
  {
    id: "real-estate-developer",
    name: "Real Estate Developer",
    category: "none",
    traits: ["charming"],
    notes: "Sold homes at premium prices and knew every neighborhood block by bus stop - then found out 'good location' does not mean survivable.",
  },
  {
    id: "tech-retail-clerk",
    name: "Tech Retail Clerk",
    category: "none",
    traits: ["charming"],
    notes: "Answered the same troubleshooting question daily with disarming confidence, usually making you feel good before you fixed anything.",
  },
  {
    id: "database-admin",
    name: "Database Admin",
    category: "none",
    traits: ["observant"],
    notes: "Kept your data clean and caught anomalies before anyone else noticed them, with the same ruthless consistency.",
  },
  {
    id: "smart-home-technician",
    name: "Smart Home Technician",
    category: "none",
    traits: [],
    notes: "Installed and maintained smart home installations and home theaters for well... mostly rich people I guess.",
  },
  {
    id: "field-network-technician",
    name: "Field Network Technician",
    category: "none",
    traits: ["observant", "calm"],
    notes: "Repaired connectivity with wire, weather, and a calm that stayed useful long enough to spot noise spikes before alarms ever rang.",
  },
  {
    id: "bigco-sr-staff-eng",
    name: "BigCo Sr Staff Eng",
    category: "none",
    traits: ["abrasive"],
    notes: "Their TC had been higher than most professional athletes, and they would always remind you, even then, when money didn't matter.",
  },
  {
    id: "prompt-engineer",
    name: "Prompt Engineer",
    category: "none",
    traits: ["slow learner"],
    notes: "Had mostly lived off vibes and occasional rescue from someone else's stack; usually needed a second run-through before the prompt landed.",
  },
  {
    id: "software-engineering-manager",
    name: "Software Engineering Manager",
    category: "none",
    traits: [],
    notes: "Spent years as a software engineer before being pushed into management. Was probably going to get laid off due to AI until the apocalypse happened!",
  },
  {
    id: "kindergarten-teacher",
    name: "Kindergarten Teacher",
    category: "none",
    traits: ["charming", "observant"],
    notes: "Kept a room of twelve priorities from collapsing into a riot, and made chaos look like a normal Tuesday.",
    poiReveal: { matchers: [{ tag: "amenity", value: "school" }], relation: "the school they used to teach at" },
  },
  {
    id: "receptionist-front-desk-clerk",
    name: "Receptionist / Front Desk Clerk",
    category: "none",
    traits: ["charming"],
    notes: "Handled nonstop inbound requests with disarming calm and routed everyone to someone else with a smile.",
  },
  {
    id: "executive-assistant",
    name: "Executive Assistant",
    category: "none",
    traits: ["calm", "quick learner"],
    notes: "Juggled calendars, meetings, and last-minute drama while somehow anticipating emergencies before they went public.",
  },
  {
    id: "paralegal",
    name: "Paralegal",
    category: "none",
    traits: ["observant"],
    notes: "Tracked paperwork, deadlines, and contradictory instructions with a precision that made missed details feel like character flaws.",
  },
  {
    id: "store-operations-supervisor",
    name: "Store Operations Supervisor",
    category: "none",
    traits: ["calm", "charming"],
    notes: "Balanced staff, queues, and inventory disruptions until the floor still moved - on time, somehow, against all evidence.",
  },
  {
    id: "warehouse-fulfillment-manager",
    name: "Warehouse Fulfillment Manager",
    category: "none",
    traits: ["observant", "abrasive"],
    notes: "Handled volume, routes, and labor at breakneck speed and caught every mistake before the floor did.",
  },
  {
    id: "air-traffic-controller",
    name: "Air Traffic Controller",
    category: "none",
    traits: ["calm", "observant"],
    notes: "Managed sequencing and timing while dozens of moving actors depended on clear, decisive instructions that couldn't wait.",
  },
  {
    id: "priest",
    name: "Priest",
    category: "none",
    traits: ["calm"],
    notes: "Held together grief, weddings, and everything in between with the same steady voice, week after week.",
    poiReveal: { matchers: [{ tag: "amenity", value: "place_of_worship" }], relation: "their church" },
  },
  {
    id: "youth-group-leader",
    name: "Youth Group Leader",
    category: "none",
    traits: ["charming"],
    notes: "Kept a rec room of restless teenagers actually showing up on purpose, every single week, somehow.",
    poiReveal: { matchers: [{ tag: "amenity", value: "place_of_worship" }], relation: "the church where they ran youth group" },
  },
  {
    id: "mechanic",
    name: "Mechanic",
    category: "none",
    traits: ["resourceful"],
    notes: "Could diagnose an engine by sound alone and kept half the neighborhood's cars running past when they should've quit.",
    poiReveal: { matchers: [{ tag: "shop", value: "car_repair" }], relation: "the auto shop they used to work at" },
  },
];

export const SURVIVOR_PROFESSION_DEFINITIONS = Object.fromEntries(
  [
    ...MEDICAL_PROFESSIONS,
    ...CONSTRUCTION_PROFESSIONS,
    ...DRIVING_PROFESSIONS,
    ...ELECTRICIAN_PROFESSIONS,
    ...COOKING_PROFESSIONS,
    ...FARMER_PROFESSIONS,
    ...LOCKSMITH_PROFESSIONS,
    ...FLAVOR_PROFESSIONS,
  ].map((profession) => [profession.id, profession]),
);

export const SURVIVOR_PROFESSION_IDS = Object.keys(SURVIVOR_PROFESSION_DEFINITIONS);

// A profession's V4 unlock category ("none" if it doesn't grant one).
export function professionCategory(professionId) {
  return SURVIVOR_PROFESSION_DEFINITIONS[professionId]?.category || "none";
}

// Every profession id tagged with a given category — e.g. for seeded-random
// assignment restricted to "give me any locksmith flavor."
export function professionIdsByCategory(category) {
  return SURVIVOR_PROFESSION_IDS.filter((id) => professionCategory(id) === category);
}

export function professionName(professionId) {
  return SURVIVOR_PROFESSION_DEFINITIONS[professionId]?.name || null;
}

export function professionNotes(professionId) {
  return SURVIVOR_PROFESSION_DEFINITIONS[professionId]?.notes || "";
}

// The profession's poiReveal matcher spec (see domain/poi-reveal.mjs), or
// null for the (most) professions that don't have one yet.
export function professionPoiReveal(professionId) {
  return SURVIVOR_PROFESSION_DEFINITIONS[professionId]?.poiReveal || null;
}

export function categoryDetails(category) {
  return PROFESSION_CATEGORY_DETAILS[category] || PROFESSION_CATEGORY_DETAILS.none;
}
